import React from 'react';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import PageNotFound from 'screens/PageNotFound';
import { AuthStateStorageService, LoadingSpinner, Header } from 'common.ui';
import RoutePaths from 'RoutePaths';
import PasswordReset from 'screens/password-reset/PasswordReset';
import ChangeSigninEmail from 'screens/change-signin-email/ChangeSigninEmail';
import { useMsal } from 'auth/MsalContext';
import { flattenRoutes, isValidRoutePath, RouteItem, toSidebarItem } from 'helpers/routing';
import ClearCookies from 'screens/dev-tools/ClearCookies';
import DevTools from 'screens/dev-tools/DevTools';
import { MainContent } from 'components/pageTemplate/MainContent';
import { HamburgerMenu } from 'components/pageTemplate/HamburgerMenu';
import LoginInternal from 'screens/login-internal/LoginInternal';
import LoginStatsforvalteren from 'screens/login-statsforvalteren/LoginStatsforvalteren';
import PostLogout from 'screens/post-logout/PostLogout';
import { useAccessControlList } from 'hooks/useAccessControlList';
import { useUser } from 'contexts/UserContext';
import { Footer } from 'components/pageTemplate/Footer';
import { useTranslation } from 'react-i18next';
import { Language } from 'api/minside';
import UnderMaintenance from 'screens/undermaintenance/UnderMaintenance';
import NoAccessPage from 'screens/NoAccessPage';
import { useCurrentUserQuery } from 'api/hooks/useCurrentUserQuery';
import { RouteConfig } from './RouteConfig';

import logoImage from './assets/images/MdirLogo_Main.svg';
import logoImageEn from './assets/images/MdirLogo_Main_En.svg';
import accountImage from './assets/images/person-header.png';
import Environment from 'Environment';

const allRoutes = flattenRoutes(RouteConfig);

const Routes = () => {
  const history = useHistory();
  const { t } = useTranslation('components', { keyPrefix: 'pageTemplate.header' });
  const [acl] = useAccessControlList();
  const hasAccess = (item: RouteItem) => {
    if (!acl) return item.requiredPermission === undefined;
    if (!item.requiredPermission) return true;
    return acl.some((a) => a === item.requiredPermission);
  };
  const { isAuthenticated, user, authInProgress, logout, login } = useMsal();
  // Uten acl slipper hasAccess gjennom alt som ikke krever en rettighet, så en utlogget bruker
  // fikk full sidemeny. Alle punktene peker på innlogget innhold, så det ser bare ut som
  // brukeren er logget inn - tydeligst på /post-logout og /password-reset.
  const sidebarItems = isAuthenticated
    ? RouteConfig.filter((x) => hasAccess(x) && !x.hidden).map((rc) => toSidebarItem(rc))
    : [];
  // isInitialLoading, ikke isLoading: en deaktivert react-query-spørring rapporterer
  // isLoading = true, og ville gitt uinnloggede brukere en evig LoadingSpinner.
  const { data: currentUser, isInitialLoading: isLoadingCurrentUser } = useCurrentUserQuery(isAuthenticated);

  const { isLoading, hasAnyAccessRights, preferredLangauge } = useUser();

  const homeRoute = allRoutes.filter((x) => x.link === RoutePaths.profile)[0];
  const getLocalizedLogo = (lang: Language) => (lang === Language.English ? logoImageEn : logoImage);

  const location = useLocation();
  const isMaintenancePage = location.pathname === RoutePaths.underMaintenance;

  if (isMaintenancePage) return <UnderMaintenance />;

  // currentUser må være lastet før noe rendres. Ruteguarden og hjem-redirecten under leser
  // employer, og på tom verdi committes en Redirect - ikke bare en flash. Uten dette blir
  // en bruker uten arbeidsgiver sendt til feil rute hvis de andre kallene resolver først.
  return authInProgress || isLoading || isLoadingCurrentUser ? (
    <LoadingSpinner />
  ) : (
    <>
      <Header
        // centered
        applicationName='MinSide'
        logoImage={getLocalizedLogo(preferredLangauge)}
        accountImage={accountImage}
        authInProgress={authInProgress}
        isAuthenticated={isAuthenticated}
        activeUser={user}
        logout={logout}
        login={login}
        accountImageClick={() => {
          history.push(RoutePaths.profile);
        }}
        loginText={t('loginText')}
        logoutText={t('logoutText')}
        showTestBanner={Environment.Current === 'test'}
        // Header viser hamburger-knappen så lenge den får et element, så uten punkter sender vi
        // ingenting - ellers åpner knappen en tom meny.
        hamburgerMenu={sidebarItems.length > 0 ? <HamburgerMenu routes={sidebarItems} /> : undefined}
      />

      <MainContent routes={sidebarItems}>
        <>
          <Switch>
            {allRoutes.map((r) => {
              if (currentUser?.employer === '' && (!r.link.includes('profile') || r.link.includes('validate')))
                return <Route exact key={r.link} path={r.link} component={() => <NoAccessPage />} />;
              return <Route exact key={r.link} path={r.link} component={hasAccess(r) ? r.component : () => <div />} />;
            })}
            {/* Redirect from password reset */}
            <Route exact key={RoutePaths.passwordReset} path={RoutePaths.passwordReset} component={PasswordReset} />
            {/* Landingsside etter fullført e-postbytte i B2C */}
            <Route
              exact
              key={RoutePaths.changeSigninEmail}
              path={RoutePaths.changeSigninEmail}
              component={ChangeSigninEmail}
            />
            <Route exact key={RoutePaths.clearCookies} path={RoutePaths.clearCookies} component={ClearCookies} />
            {Environment.Current === 'local' && (
              <Route exact key={RoutePaths.devTools} path={RoutePaths.devTools} component={DevTools} />
            )}
            <Route exact key={RoutePaths.loginInternal} path={RoutePaths.loginInternal} component={LoginInternal} />
            <Route
              exact
              key={RoutePaths.loginStatsforvalteren}
              path={RoutePaths.loginStatsforvalteren}
              component={LoginStatsforvalteren}
            />
            <Route exact key={RoutePaths.postLogout} path={RoutePaths.postLogout} component={PostLogout} />

            <Route
              exact
              key={RoutePaths.home}
              path={RoutePaths.home}
              render={() => {
                if (!isAuthenticated) return React.createElement(homeRoute.component);
                const redirectTo = AuthStateStorageService.getRedirectionState();

                if (redirectTo && isValidRoutePath(redirectTo)) {
                  AuthStateStorageService.setRedirectionState(undefined);
                  return <Redirect to={redirectTo} />;
                }

                if (currentUser?.employer === '') return <Redirect to={RoutePaths.profileConfigureEmployer} />;

                return hasAnyAccessRights ? (
                  <Redirect to={RoutePaths.profile} />
                ) : (
                  <Redirect to={RoutePaths.accessRequest} />
                );
              }}
            />

            {/* 404 route */}
            <Route component={PageNotFound} />
          </Switch>

          <Footer centered logoImage={getLocalizedLogo(preferredLangauge)} />
        </>
      </MainContent>
    </>
  );
};

export { Routes };
